/**
 * Axis Utilities
 *
 * Shared helpers for building double/secondary x-axis and y-axis
 * configurations from dashboard JSON config.
 *
 * Config keys consumed:
 *   enableDoubleXAxis   – show secondary x-axis
 *   enableXAxis2Click   – make secondary x-axis clickable
 *   doublexAxisKey      – filter key sent in API call on click
 *   enableDoubleYAxis   – show secondary y-axis
 *   enableYAxis2Click   – make secondary y-axis clickable
 *   doubleyAxisKey      – filter key for y-axis click (falls back to doublexAxisKey)
 *   chartConfig.xAxis2GroupRegex – regex to extract group from category string
 */

// ── Group extraction ────────────────────────────────────────────────

/**
 * Extract the secondary-axis group value from a category string.
 *
 * @param cat       The category label, e.g. "Q1 2025"
 * @param regexStr  Custom regex (from chartConfig.xAxis2GroupRegex).
 *                  Defaults to year pattern `(19|20)\d{2}`.
 * @returns         The matched group, e.g. "2025", or empty string.
 *
 * Examples with different regexStr:
 *   "(19|20)\\d{2}"   → "Q1 2025" → "2025"  (year – default)
 *   "^[A-Za-z]+"      → "Jan North" → "Jan"  (first word)
 *   "\\S+$"           → "Week1 RegionA" → "RegionA" (last word)
 */
export function extractGroupFromCategory(cat: string, regexStr?: string): string {
  const pattern = regexStr || '(19|20)\\d{2}';
  const m = String(cat).match(new RegExp(pattern));
  return m ? m[0] : '';
}

// ── Secondary axis groups ───────────────────────────────────────────

export interface AxisGroupResult {
  /** One entry per category; non-empty only at the center of each group. */
  labels: string[];
  /** Indices where group boundaries fall (used for tick marks). */
  tickPositions: number[];
}

/**
 * Build secondary-axis group labels and tick positions from category strings.
 *
 * E.g. ["Q4 2023","Q1 2024","Q2 2024","Q3 2024","Q4 2024","Q1 2025"]
 * → labels: ["2023","","2024","","","2025"], tickPositions: [1, 5]
 *
 * @param categories  Primary x-axis category array
 * @param regexStr    Regex to extract group (from chartConfig.xAxis2GroupRegex)
 */
export function buildSecondaryAxisGroups(
  categories: string[],
  regexStr?: string,
): AxisGroupResult {
  const groupValues = categories.map((cat) => extractGroupFromCategory(cat, regexStr));

  // If no category matched the regex, there's nothing to group
  if (groupValues.every((v) => v === '')) {
    return { labels: new Array(categories.length).fill(''), tickPositions: [] };
  }

  // Build contiguous groups: { value, startIdx, endIdx }
  // Use a unique sentinel so the first iteration always creates a new group entry
  const groups: { value: string; start: number; end: number }[] = [];
  let currentGroup: string | null = null;
  for (let i = 0; i < groupValues.length; i++) {
    if (groupValues[i] !== currentGroup) {
      if (groups.length > 0) {
        groups[groups.length - 1].end = i - 1;
      }
      currentGroup = groupValues[i];
      groups.push({ value: currentGroup, start: i, end: i });
    } else {
      groups[groups.length - 1].end = i;
    }
  }

  // Labels array: empty for all positions, label at center of each group
  const labels: string[] = new Array(categories.length).fill('');
  const tickPositions: number[] = [];

  groups.forEach((group, idx) => {
    const centerIdx = Math.floor((group.start + group.end) / 2);
    labels[centerIdx] = group.value;

    // Tick at the start of each group (boundary line), skip first group
    if (idx > 0) {
      tickPositions.push(group.start);
    }
  });

  return { labels, tickPositions };
}

// ── ECharts axis option builders ────────────────────────────────────

/**
 * Build a secondary ECharts x-axis config object.
 * Returns `null` if `enableDoubleXAxis` is falsy.
 *
 * @param data            Widget config from dashboard JSON
 * @param categories      Primary x-axis category array
 * @param explicitLabels  Optional explicit labels (e.g. country names from bubble chart data).
 *                        When provided, these are used directly instead of regex extraction.
 * @returns               Secondary axis config, or null
 */
export function buildSecondaryXAxisOption(
  data: any,
  categories: string[],
  explicitLabels?: string[],
): { axis: any; labels: string[] } | null {
  if (!data?.enableDoubleXAxis) return null;

  let labels: string[];
  let tickPositions: number[] = [];

  if (explicitLabels && explicitLabels.length > 0) {
    // Use explicit labels directly (each label gets its own position)
    labels = explicitLabels;
  } else {
    const regexStr = data?.chartConfig?.xAxis2GroupRegex;
    const axisGroups = buildSecondaryAxisGroups(categories, regexStr);

    // If no groups were extracted, skip the secondary axis
    if (axisGroups.labels.every((l) => l === '')) return null;

    labels = axisGroups.labels;
    tickPositions = axisGroups.tickPositions;
  }

  const secondAxis: any = {
    type: 'category',
    data: labels,
    triggerEvent: true,
    position: 'bottom',
    offset: 25,
    axisLine: { show: false },
    axisTick: {
      show: true,
      length: tickPositions.length > 0 ? 15 : 0,
      lineStyle: { color: '#ccc' },
      alignWithLabel: false,
      interval: (index: number) => tickPositions.includes(index),
    },
    axisLabel: {
      clickable: true,
      fontWeight: 'bold',
      fontSize: 12,
      interval: 0,
    },
  };

  return { axis: secondAxis, labels };
}

/**
 * Build a secondary ECharts y-axis config object.
 * Returns `null` if `enableDoubleYAxis` is falsy.
 *
 * @param data  Widget config from dashboard JSON
 * @param yAxis2FormatterFn  Optional custom formatter for the axis label
 */
export function buildSecondaryYAxisOption(
  data: any,
  yAxis2FormatterFn?: (value: number) => string,
): any | null {
  if (!data?.enableDoubleYAxis) return null;

  const yAxis2Cfg = data?.chartConfig?.yAxis2 || {};

  const secondAxis: any = {
    type: 'value',
    position: 'right',
    triggerEvent: true,
    axisLabel: {
      show: true,
      ...(yAxis2FormatterFn ? { formatter: yAxis2FormatterFn } : {}),
    },
    axisLine: { show: true, lineStyle: { color: '#999' } },
    splitLine: { show: false },
    ...(yAxis2Cfg?.name
      ? {
          name: yAxis2Cfg.name,
          nameLocation: yAxis2Cfg.nameLocation || 'center',
          nameGap: yAxis2Cfg.nameGap || 40,
        }
      : {}),
  };

  return secondAxis;
}

// ── Grid helpers ────────────────────────────────────────────────────

/**
 * Get extra grid spacing needed when double axes are enabled.
 */
export function getDoubleAxisGridAdjustments(data: any): {
  extraBottom: number;
  extraRight: number;
} {
  return {
    extraBottom: data?.enableDoubleXAxis ? 4 : 0,
    extraRight: data?.enableDoubleYAxis ? 4 : 0,
  };
}
